import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { JobTemplateQuestion } from '@jms/model';
import { resolveItemFromRow } from '@workbuddy/entities/shared/items';
import { BaseCellRenderer } from '@workbuddy/ui/cell.renderers';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { openJobTemplateQuestionVisibilityDialog } from '../../../dialogs/question.visibility';
import { JOB_TEMPLATE_QUESTION_SCOPE, type JobTemplateQuestionBoardParams } from '../../../job.template.question.model';
import { jobTemplateQuestionShowConditionLabel } from './question.show.condition.label';

export interface QuestionShowConditionCellParams extends ICellRendererParams {
  boardParams?: JobTemplateQuestionBoardParams;
}

/**
 * The visibility rule as legacy's grid shows it: whether the question is asked always or on a
 * condition, and the only place the rule is opened from.
 *
 * The click stops DOM propagation because the board wires ONE grid-level `onCellClicked` that routes
 * every cell to `itemInteraction.navigate` — without it, this cell would open the editor as well.
 * href="#" + preventDefault makes the UA treat the label as a link (legacy used `<a class="question-name">`).
 */
@Component({
  selector: 'wb-question-show-condition-cell',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    wb-question-show-condition-cell {
      display: flex;
      align-items: center;
      height: 100%;

      a.show-condition-link {
        color: var(--wb-color-link, #2563eb);
        text-decoration: none;
        cursor: pointer;
      }

      a.show-condition-link:hover {
        text-decoration: underline;
      }
    }
  `],
  template: `<a class="show-condition-link" href="#" (click)="onOpen($event)">{{ $label() }}</a>`,
})
export class QuestionShowConditionCellRenderer extends BaseCellRenderer<unknown, QuestionShowConditionCellParams> {

  protected readonly $label = signal<string>('Always');

  private readonly modals = inject(WorkBuddyDialogService);

  private question: JobTemplateQuestion | null = null;
  private boardParams: JobTemplateQuestionBoardParams | null = null;

  public displayLogic(params: QuestionShowConditionCellParams): void {

    this.question = resolveItemFromRow(params.data, JOB_TEMPLATE_QUESTION_SCOPE) as unknown as JobTemplateQuestion | null;
    this.boardParams = params.boardParams ?? null;
    this.$label.set(jobTemplateQuestionShowConditionLabel(this.question));
  }

  protected onOpen(event: MouseEvent): void {

    event.preventDefault();
    event.stopPropagation();

    if (!this.question?._id || !this.boardParams) return;
    openJobTemplateQuestionVisibilityDialog(this.modals, this.boardParams, this.question._id);
  }
}
